/**
 * Changing a customer's plan from the console (redesign phase 7, decision D1).
 *
 * ── The screen's job is to say WHERE the change goes, before it goes ────
 *
 * There are two mechanisms and the tenant's state picks one:
 *
 *   **provider** — an active, Stripe-backed subscription. The change is made at
 *   Stripe; an upgrade swaps immediately with prorations, a downgrade is
 *   scheduled for the period boundary so the customer keeps what they paid for.
 *   The webhook mirror lands the truth, so we stay reconciled by construction.
 *
 *   **local** — everything else: a free tenant, a customer invoiced by bank
 *   transfer, a subscription mid-dunning. The plan is written to
 *   `tenants.platform_plan_override` and from that moment our record DIVERGES
 *   from the payment processor.
 *
 * A deployment with billing switched off is NOT a third case: the routes behind
 * this panel are flag-gated, because an override there would move the plan name
 * and no limit under it (`applyEntitlements` no-ops, the model-access plan layer
 * is off). So `billingEnabled` is true wherever this renders, and there is
 * deliberately no copy for the other value — copy that cannot be reached is copy
 * nobody checks, and it would assert something the code contradicts.
 *
 * An operator who cannot tell those apart will "downgrade" a paying customer,
 * see a success toast, and discover hours later that Stripe never heard about
 * it. So the path is stated up front, not reported afterwards — and it is read
 * from the server, computed by the same function the write uses, rather than
 * guessed at from the subscription shape.
 *
 * ── The one state with no name anywhere else ────────────────────────────
 *
 * `override.shadowed`: a local override is stored AND a provider-backed
 * subscription outranks it, so an operator's explicit decision is silently
 * doing nothing. Deliberate — an override that could take away what a customer
 * is being charged for is the one failure money makes unrecoverable. But it
 * must be visible, or the console shows a plan the product is not honouring.
 */
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { useToast, useRefreshWhenShown, useDraftState } from '@papan/core-ui/ui'
import { formatCurrencyMinor, formatPlatformDateLocal } from '@papan/types'
import { planAdminApi, type CustomerPlanState } from '../client/plan-admin.js'
import { catalogAdminApi } from '../client/catalog-admin.js'

export function CustomerPlan({ tenantId, canEdit, onChanged }: {
  tenantId: string
  canEdit: boolean
  /**
   * Fired after a successful change so the PAGE can reload its own row.
   *
   * The header, the plan badge and the seat block are fed by a different
   * endpoint from this panel, and reloading only this one left them reading
   * "Pro (set here)" for a customer whose override had just been removed —
   * two figures for one fact, on one screen, which is the failure this whole
   * redesign keeps closing. Not optional in practice; optional in the type so a
   * future caller that renders the panel alone is not forced to invent one.
   */
  onChanged?: () => void
}) {
  const { t } = useTranslation('billing')
  const toast = useToast()
  const [data, setData] = useState<CustomerPlanState | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // The plan picked but not yet applied — a draft over the effective plan
  // (#897, kept across a phone/desktop layout swap). Until picked it IS the
  // effective plan, so every `load` (including a re-show's) shows through; once
  // picked, no `load` overwrites it. An applied change drops it.
  const [target, setTarget, { clear: clearTarget }] =
    useDraftState<string>(`customerPlan:${tenantId}`, () => data?.effective.planId ?? '')
  const [saving, setSaving] = useState(false)
  const [reapplying, setReapplying] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const next = await planAdminApi.get(tenantId)
      setData(next)
      setError(null)
    } catch (e: any) {
      setError(e?.message || 'Failed to load the plan')
    } finally {
      setLoading(false)
    }
  }, [tenantId])

  useEffect(() => { void load() }, [load])

  // The shelf/settings deck stays MOUNTED behind `display: none`, so the mount
  // effect above is the only fetch this page would ever make — it would serve
  // whatever was true when the deck was first opened, with no error and no way
  // back but a full reload. See `DeckVisibility`.
  useRefreshWhenShown(() => { void load() })

  if (loading && !data) {
    return <div className="themed-text-muted text-sm">{t('common:loading')}</div>
  }
  if (!data) return <div className="text-sm text-red-400">{error}</div>

  const provider = data.path === 'provider'
  // On the provider path a plan with no Stripe price cannot be the target, and
  // neither can a free one — that route is a cancellation, which is a different
  // action with a different confirmation. Disable rather than hide, with the
  // reason: a missing plan looks like a catalog change, a disabled one looks
  // like the deployment gap it is.
  const selectable = (p: CustomerPlanState['plans'][number]) =>
    p.isActive && (!provider || (p.syncedToProvider && p.seatPriceMinor > 0))

  const dirty = target !== data.effective.planId
  // Offered whenever an override EXISTS, on either path. On the provider path
  // it is the shadow warning's own instruction ("remove the override, or change
  // the plan at the provider"), and gating it on `!provider` printed that
  // sentence beside no button to follow it with.
  const canRemoveOverride = !!data.override

  const apply = async (planId: string | null) => {
    setSaving(true)
    try {
      const res = await planAdminApi.set(tenantId, planId)
      if (res.unchanged) {
        toast.success(t('adminPlan.unchanged', { defaultValue: 'Already on that plan — nothing to change.' }))
      } else if (res.revertedPendingChange) {
        toast.success(t('adminPlan.reverted', {
          defaultValue: 'The scheduled downgrade was cancelled; the tenant stays on their current plan.',
        }))
      } else if (res.path === 'provider') {
        toast.success(res.effective === 'period_end'
          ? t('adminPlan.doneScheduled', {
              defaultValue: 'Scheduled at the payment provider. It takes effect at the end of the paid period.',
            })
          : t('adminPlan.doneImmediate', {
              defaultValue: 'Changed at the payment provider, effective now, with prorations.',
            }))
      } else if (planId === null) {
        toast.success(t('adminPlan.doneRemoved', {
          defaultValue: 'Override removed. This tenant falls back to their own plan.',
        }))
      } else {
        toast.success(t('adminPlan.doneLocal', {
          defaultValue: 'Set here, effective now. This does not reach the payment provider.',
        }))
      }
      await load()
      clearTarget()
      onChanged?.()
    } catch (e: any) {
      toast.error(e?.message || 'Plan change failed')
    } finally {
      setSaving(false)
    }
  }

  /**
   * Re-materialize this tenant's limits from their current plan (A4).
   *
   * Moved here from the account card by F-30, because it is a PLAN action and
   * belonged beside the plan rather than beside the seat count: the tab stated
   * the plan in four places and each one carried a different control, so
   * finding out what you could do to a tenant's plan meant reading all four.
   *
   * Entitlements are COPIED onto the tenant at subscription events, not read
   * through from the plan per request — so a plan edit reaches existing
   * subscribers only when something re-triggers that. This is the re-trigger.
   *
   * Always offered rather than shown only when stale: there is no cheap way to
   * know whether this tenant is stale (the question is "has the plan changed
   * since THIS tenant's last entitlement event"), and re-applying is idempotent
   * — it recomputes from the same inputs `applyEntitlements` always uses.
   *
   * It does NOT overwrite a platform admin's own ceiling. The plan writes the
   * GRANTED allowance (`free_cap_usd` / `extra_cap_usd`); an operator's ceiling
   * lives in the window-keyed JSONB beside it and the two are combined by a
   * `min()` at read time. Were that not true this button would be a way to
   * silently undo a deliberate cap with one click.
   */
  const reapply = async () => {
    setReapplying(true)
    try {
      const res = await catalogAdminApi.reapplyEntitlements(tenantId)
      // `applyEntitlements` returns null when BILLING_ENABLED is off — the whole
      // entitlement layer is a no-op there, deliberately, so that ordinary org
      // administration cannot start rewriting budget ceilings on an install that
      // never opted into billing. Reporting success would be a straight lie, and
      // the operator would then go looking for why the caps had not moved.
      if (res.applied == null) {
        toast.info(t('admin.account.reapplyNoop', {
          defaultValue: 'Nothing to apply — billing is switched off on this deployment, so plan limits are not materialized onto tenants.',
        }))
      } else {
        toast.success(t('admin.account.reapplied', {
          defaultValue: 'Limits re-applied from the current plan.',
        }))
      }
      await load()
      // The ceilings on the Configuration tab are what just moved, so the PAGE
      // has to reload — not only this panel.
      onChanged?.()
    } catch (e: any) {
      toast.error(e?.message || 'Re-apply failed')
    } finally {
      setReapplying(false)
    }
  }

  return (
    <div className="themed-bg-secondary rounded-lg p-3">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs themed-text-secondary">
            {t('adminPlan.current', { defaultValue: 'Plan' })}
          </div>
          {/* The DISPLAY name, not the preset's — "Custom" for a hand-edited
              tenant, exactly as the customer header and the Configuration tab
              render it. This showed `planName` raw until 2026-08-18, so one
              page gave two answers to "what plan is this?": "MAX" here and
              "Custom based on MAX" two tabs over.

              `?? planName` because the field is optional on the wire — an
              older API during a split deploy falls back to the previous
              behaviour rather than rendering nothing. */}
          <div className="themed-text text-sm">
            {data.effective.label ?? data.effective.planName}
            <SourceBadge source={data.effective.source} />
          </div>
          {/* The preset beneath it, and only when the label is NOT the preset's
              name — a standard customer must not carry a redundant
              "based on Free". Same rule and same string as Configuration. */}
          {data.custom?.isCustom && data.effective.planName && (
            <div className="themed-text-muted text-[11px]">
              {t('adminPlan.basedOn', {
                defaultValue: 'based on {{preset}}', preset: data.effective.planName,
              })}
            </div>
          )}
        </div>
        <PathNote path={data.path} subscription={data.subscription} />
      </div>

      {/* Says WHICH families differ rather than only that something does — the
          operator's next question is "edited how?", and the answer decides
          whether they open Configuration at all. */}
      {data.custom?.isCustom && (
        <p className="text-[11px] themed-text-muted mt-2">
          {t('adminPlan.customNote', {
            defaultValue:
              'Their {{families}} are set for this tenant and override {{plan}}. "Put back on the preset" on Configuration discards them.',
            families: data.custom.reasons
              .map((r) => t(`adminPlan.customReason.${r}`, {
                defaultValue: r === 'ceilings' ? 'ceilings' : r === 'models' ? 'model restrictions' : 'prices',
              }))
              .join(', '),
            plan: data.effective.planName,
          })}
        </p>
      )}

      {data.subscription?.pendingPlanId && (
        <p className="text-[11px] text-amber-300 mt-2">
          {t('adminPlan.pending', {
            defaultValue: 'A change to {{plan}} is already scheduled for the end of the paid period.',
            plan: data.plans.find((p) => p.id === data.subscription!.pendingPlanId)?.name
              ?? data.subscription.pendingPlanId,
          })}
        </p>
      )}

      {data.override?.shadowed && (
        <div className="mt-3 rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2">
          <div className="text-amber-300 text-xs font-medium">
            {t('adminPlan.shadowedTitle', { defaultValue: 'An override here is doing nothing' })}
          </div>
          <p className="themed-text-secondary text-[11px] mt-0.5">
            {t('adminPlan.shadowedBody', {
              defaultValue:
                'This tenant is set to {{plan}} here, but they have a live subscription with the payment provider and that wins — otherwise we would be charging them for something they are not getting. Remove the override, or change the plan at the provider.',
              plan: data.override.planName ?? data.override.planId,
            })}
          </p>
        </div>
      )}

      {data.override && !data.override.shadowed && (
        <p className="text-[11px] themed-text-muted mt-2">
          {t('adminPlan.overrideSetAt', {
            defaultValue: 'Set here on {{date}}. It does not reach the payment provider.',
            date: data.override.at ? formatPlatformDateLocal(data.override.at) : '—',
          })}
        </p>
      )}

      {canEdit ? (
        <div className="mt-3 pt-3 border-t themed-border">
          <label className="themed-text-secondary text-[11px] block mb-1">
            {t('adminPlan.changeTo', { defaultValue: 'Change to' })}
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              disabled={saving}
              aria-label={t('adminPlan.changeTo', { defaultValue: 'Change to' })}
              className="themed-input border rounded px-2 py-1 text-xs outline-none focus:border-blue-500 disabled:opacity-50"
            >
              {data.plans.map((p) => (
                <option key={p.id} value={p.id} disabled={!selectable(p)}>
                  {p.name}
                  {p.seatPriceMinor > 0 ? ` — ${formatCurrencyMinor(p.seatPriceMinor, p.currency)}` : ''}
                  {!p.isActive ? ' (retired)' : provider && !p.syncedToProvider ? ' (not synced)' : ''}
                </option>
              ))}
            </select>
            <button
              onClick={() => apply(target)}
              disabled={saving || !dirty}
              className="px-3 py-1.5 text-xs rounded themed-btn-primary disabled:opacity-50"
            >
              {saving ? t('common:saving') : t('adminPlan.apply', { defaultValue: 'Apply' })}
            </button>
            {canRemoveOverride && (
              <button
                onClick={() => apply(null)}
                disabled={saving}
                className="px-2.5 py-1 text-xs rounded themed-btn-secondary disabled:opacity-50"
              >
                {t('adminPlan.remove', { defaultValue: 'Stop overriding — follow the payment provider' })}
              </button>
            )}
          </div>
          {provider && (
            <p className="text-[11px] themed-text-muted mt-2">
              {t('adminPlan.providerHint', {
                defaultValue:
                  'An upgrade takes effect now with prorations; a downgrade waits for the end of the period the tenant has already paid for. To move them off a paid plan entirely, cancel at the provider.',
              })}
            </p>
          )}

          {/* Re-apply, beneath the change control rather than beside it: it is
              not an alternative way to change the plan, it is how a tenant
              already ON this plan picks up an edit made to the plan itself. */}
          <div className="mt-3 pt-3 border-t themed-border">
            <button
              onClick={reapply}
              disabled={reapplying || saving}
              className="px-2.5 py-1 text-xs rounded themed-btn-secondary disabled:opacity-50"
            >
              {reapplying
                ? t('admin.account.reapplying', { defaultValue: 'Re-applying…' })
                : t('admin.account.reapply', { defaultValue: 'Re-apply plan limits' })}
            </button>
            <p className="text-[11px] themed-text-muted mt-1">
              {t('admin.account.reapplyBody', {
                defaultValue:
                  'Budget, storage quota and concurrent agent jobs are copied onto a tenant when their subscription changes — so editing a plan does not move an existing tenant until this is run.',
              })}
            </p>
          </div>
        </div>
      ) : (
        <p className="text-[11px] themed-text-muted mt-3">
          {t('adminPlan.readOnly', { defaultValue: 'Changing a plan needs write access to Tenants.' })}
        </p>
      )}
    </div>
  )
}

/** Which layer decided the plan in force. Not decoration: `override` is the
 *  one that means "this figure exists only here". */
function SourceBadge({ source }: { source: CustomerPlanState['effective']['source'] }) {
  const { t } = useTranslation('billing')
  if (source === 'subscription') return null
  const label = source === 'override'
    ? t('adminPlan.srcOverride', { defaultValue: 'set here' })
    : t('adminPlan.srcFree', { defaultValue: 'default' })
  return (
    <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] themed-bg-tertiary themed-text-muted align-middle">
      {label}
    </span>
  )
}

/** Where the next change will go. Stated BEFORE the control, because it is the
 *  fact that decides whether this action reaches the payment processor. */
function PathNote({ path, subscription }: {
  path: 'provider' | 'local'
  subscription: CustomerPlanState['subscription']
}) {
  const { t } = useTranslation('billing')
  // The local path has TWO causes and they are not interchangeable. "No live
  // subscription to change" is simply FALSE for a customer mid-dunning: they
  // have a provider-backed subscription, it is just not `active`, so
  // `outranksPlanOverride` routes the change here rather than to Stripe.
  // Saying otherwise on a money screen is the kind of small lie an operator
  // acts on — and it went unseen until a `past_due` row existed to render it,
  // which is DEF-16 in one sentence.
  const dunning = path === 'local' && !!subscription?.providerBacked
  // ONE sentence, not a small-caps label with the rest of its own sentence
  // right-aligned beneath it. The split rendered as "A CHANGE HERE / is
  // recorded here only — …", which reads as broken markup rather than as
  // English, on the line whose entire job is to say whether the next click
  // reaches the payment processor.
  return (
    <div className="text-right max-w-[22rem] themed-text-secondary text-[11px]">
      {path === 'provider'
        ? t('adminPlan.pathProvider', {
            defaultValue: 'A change here goes to the payment provider, and comes back through the webhook.',
          })
        : dunning
          ? t('adminPlan.pathLocalDunning', {
              defaultValue:
                'A change here is recorded here only — their subscription is {{status}}, so it is not changed at the payment provider until it recovers.',
              status: subscription!.status,
            })
          : t('adminPlan.pathLocal', {
              defaultValue: 'A change here is recorded here only — this tenant has no live subscription to change.',
            })}
    </div>
  )
}
